/* eslint-disable camelcase */
import { DateTime } from 'luxon';

// Intercom Messenger session state. Kept free of Relay so the network layer
// (src/util/environment.js) can end the session on a 401 without an import cycle.

// Whether the Messenger was booted with a signed intercom_user_jwt. The JWT then carries the
// user's identity, so updates must not resend it unsigned.
let bootedWithUserJwt = false;

// Counts boot attempts. A pending intercomHandler() boots only if no later boot or shutdown has
// happened while it waited for the JWT, so a logged-out user's JWT is never booted.
let bootGeneration = 0;

/**
 * Starts a boot attempt, invalidating any attempt still waiting for its JWT.
 * @returns {number} - the generation of this boot attempt
 */
export const getNextIntercomBootGeneration = () => {
  bootGeneration += 1;
  return bootGeneration;
};

/**
 * The generation of the latest boot attempt or shutdown.
 * @returns {number} - the current boot generation
 */
export const getIntercomBootGeneration = () => bootGeneration;

/**
 * Generates the payload to be passed to intercom.
 * Basic payload will be the current time and platform domain.
 * Will append the logged in userdetails  from the local storage(if available),
 * unless the Messenger was booted with a signed user JWT.
 * @returns {object} - payload to intercom
 */
export const getIntercomPayload = () => {
  const loggedInUser = localStorage.getItem('loggedInUser');
  const userDetails = loggedInUser ? JSON.parse(localStorage.getItem('loggedInUser')) : '';
  const basicPayload = {
    last_request_at: Math.floor(DateTime.local().toSeconds()),
    powertracer_platform_domain: window.location.hostname,
  };
  if (!userDetails || bootedWithUserJwt) {
    return basicPayload;
  }
  return { ...userDetails, ...basicPayload };
};

// A navigation usually sets two titles (the layout's, then the page's), which
// would send two Intercom updates. Each title change restarts this timer, so
// one update is sent once the title has settled. Intercom allows about 20
// updates per user per 30 minutes, so halving the calls keeps "Last seen"
// updating. 250 ms covers the gap between the two titles (a few ms) without a
// noticeable delay; the exact value is not critical.
const TITLE_UPDATE_DEBOUNCE_MS = 250;

let titleObserver;
let titleUpdateTimer;

/**
 * Stop pinging Intercom on page title changes.
 * @returns {void}
 */
const stopTitleObserver = () => {
  titleObserver?.disconnect();
  titleObserver = undefined;
  clearTimeout(titleUpdateTimer);
};

/**
 * Initiate and renders Intercom widget, identifying the logged in user (if any).
 * Updates/pings intercom with the latest info when the page title changes (ex: page navigation).
 * @param {string} intercomAppId
 * @param {string|null} [userJwt] - signed Messenger JWT; omitted from boot when absent, because
 *   Intercom refuses to load the Messenger for an invalid JWT
 * @returns {void}
 */
export const renderIntercom = (intercomAppId, userJwt = null) => {
  // eslint-disable-next-line
  (function () { var w = window; var ic = w.Intercom; if (typeof ic === "function") { ic('reattach_activator'); ic('update', w.intercomSettings); } else { var d = document; var i = function () { i.c(arguments); }; i.q = []; i.c = function (args) { i.q.push(args); }; w.Intercom = i; var l = function () { var s = d.createElement('script'); s.type = 'text/javascript'; s.async = true; s.src = `https://widget.intercom.io/widget/${intercomAppId}`; var x = d.getElementsByTagName('script')[0]; x.parentNode.insertBefore(s, x); }; if (d.readyState === 'complete') { l(); } else if (w.attachEvent) { w.attachEvent('onload', l); } else { w.addEventListener('load', l, false); } } })();

  bootedWithUserJwt = Boolean(userJwt);
  window.Intercom('boot', {
    app_id: intercomAppId,
    ...getIntercomPayload(),
    ...(userJwt ? { intercom_user_jwt: userJwt } : {}),
  });

  // React 19 hoists each page's <title> into <head> as a new element, so watch
  // the whole <head> and ping Intercom only when the document title changes.
  stopTitleObserver();
  let lastTitle = document.title;
  titleObserver = new MutationObserver(() => {
    if (document.title === lastTitle) {
      return;
    }
    lastTitle = document.title;
    clearTimeout(titleUpdateTimer);
    titleUpdateTimer = setTimeout(() => {
      window.Intercom('update', getIntercomPayload());
    }, TITLE_UPDATE_DEBOUNCE_MS);
  });
  titleObserver.observe(
    document.head,
    { subtree: true, characterData: true, childList: true },
  );
};

/**
 * Close/shutdown Intercom when user logs out or when a session ends.
 * Clears user information from the local storage.
 * @returns {void}
 */
export const shutdownIntercom = () => {
  stopTitleObserver();
  if (window.Intercom) {
    window.Intercom('shutdown');
  }
  bootedWithUserJwt = false;
  bootGeneration += 1;
  localStorage.removeItem('loggedInUser');
};
